/** The semantic input a body-effect shader needs from the human-vision pipeline. */
export type HumanVisionDetectionTarget =
  | 'person-mask'
  | 'body-contour'
  | 'pose-landmarks'
  | 'face-landmarks';

export interface HumanVisionMask {
  width: number;
  height: number;
  /** One top-to-bottom, 8-bit confidence value per mask pixel. */
  data: Uint8Array;
}

export interface HumanVisionLandmark {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

export interface HumanVisionResult {
  requestId: number;
  timestampMs: number;
  target: HumanVisionDetectionTarget;
  personDetected: boolean;
  faceDetected: boolean;
  humanMask?: HumanVisionMask;
  faceMask?: HumanVisionMask;
  poseLandmarks?: HumanVisionLandmark[];
  faceLandmarks?: HumanVisionLandmark[];
}

export interface HumanVisionDetectRequest {
  type: 'detect';
  requestId: number;
  timestampMs: number;
  target: HumanVisionDetectionTarget;
  frame: ImageBitmap;
}

export interface HumanVisionResetRequest {
  type: 'reset';
}

export type HumanVisionWorkerRequest = HumanVisionDetectRequest | HumanVisionResetRequest;

export interface HumanVisionDetectResponse {
  type: 'result';
  result: HumanVisionResult;
}

export interface HumanVisionErrorResponse {
  type: 'error';
  requestId: number;
  message: string;
}

export type HumanVisionWorkerResponse = HumanVisionDetectResponse | HumanVisionErrorResponse;

export interface HumanVisionEffectLike {
  startSeconds?: number;
  endSeconds?: number;
  params?: Record<string, unknown>;
}

const TARGET_PRIORITY: Readonly<Record<HumanVisionDetectionTarget, number>> = {
  'person-mask': 1,
  'body-contour': 2,
  'pose-landmarks': 3,
  'face-landmarks': 4,
};

export function isHumanVisionDetectionTarget(value: unknown): value is HumanVisionDetectionTarget {
  return value === 'person-mask'
    || value === 'body-contour'
    || value === 'pose-landmarks'
    || value === 'face-landmarks';
}

/** Legacy body presets did not carry a detector target. They require a person matte. */
export function humanVisionTargetForEffect(effect: HumanVisionEffectLike): HumanVisionDetectionTarget | null {
  const params = effect.params;
  if (!params) return null;
  const explicit = params['detectionTarget'];
  if (isHumanVisionDetectionTarget(explicit)) return explicit;
  if (params['requiresPerson'] === true || params['scope'] === 'body' || params['target'] === 'person') {
    return 'person-mask';
  }
  return null;
}

/** Select the strongest detector required by the effects active at this frame. */
export function requiredHumanVisionTarget(
  effects: readonly HumanVisionEffectLike[] | null | undefined,
  timeSeconds?: number,
): HumanVisionDetectionTarget | null {
  let selected: HumanVisionDetectionTarget | null = null;
  for (const effect of effects ?? []) {
    if (timeSeconds != null) {
      const start = effect.startSeconds ?? Number.NEGATIVE_INFINITY;
      const end = effect.endSeconds ?? Number.POSITIVE_INFINITY;
      if (timeSeconds < start || timeSeconds >= end) continue;
    }
    const target = humanVisionTargetForEffect(effect);
    if (target && (!selected || TARGET_PRIORITY[target] > TARGET_PRIORITY[selected])) selected = target;
  }
  return selected;
}

export function humanVisionResultSatisfiesTarget(
  result: HumanVisionResult | null | undefined,
  target: HumanVisionDetectionTarget | null | undefined,
): boolean {
  if (!result || !target || !result.personDetected || !result.humanMask) return false;
  switch (target) {
    case 'person-mask':
    case 'body-contour':
      return true;
    case 'pose-landmarks':
      return (result.poseLandmarks?.length ?? 0) >= 25;
    case 'face-landmarks':
      return result.faceDetected
        && !!result.faceMask
        && (result.faceLandmarks?.length ?? 0) > 0;
  }
}

export function humanVisionTransferables(result: HumanVisionResult): Transferable[] {
  const transferables: Transferable[] = [];
  if (result.humanMask) transferables.push(result.humanMask.data.buffer);
  if (result.faceMask) transferables.push(result.faceMask.data.buffer);
  return transferables;
}
